import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { GenericEntry } from "@shared/schema";
import { useSectionFields } from "./useSectionFields";
import { useSaveFailure } from "./saveFailure";
import { queueRowSave, rowSaveKey } from "@/lib/rowSaveQueue";

/**
 * s223 — THE ROW SURFACE for a list section the interview writes to (exec, benef, ids).
 *
 * The children screens have their own (`useChildRows`: one section, a kind column, per-row gates, two flags);
 * the three s223 screens need only the plain part of it — read a section's rows, write cells, hide a row, start
 * a row — so this is that part, ONCE, rather than three copies of it in three screens (G7).
 *
 * Writes go through the ONE planner write path, `POST /generic-entries`, per cell (D-210.18). Hiding sends
 * `isHidden` and NO value, so nothing a row held is changed. There is no DELETE here.
 */

export type SectionRow = {
  rowId: string;
  /** Every entry on the row is hidden (deselected or removed). */
  hidden: boolean;
  /** Field key → stored value, hidden entries included, so an unhide restores them. */
  values: Record<string, string>;
  /** s232 (chapter two): field key → a typed "Other" answer's words (OTHER-SPECIFY-1). Present only when one is held. */
  specify?: Record<string, string>;
  /** s232 (chapter two): the keys answered with a decline ("don't have" / "won't store") — a real answer (s230). Present only when one is held. */
  declined?: string[];
};

/** Group a section's entries into rows. Pure; order is first-written first, then row id. */
export function buildSectionRows(
  entries: readonly (Pick<GenericEntry, "rowId" | "sectionId" | "fieldId" | "value" | "isHidden" | "createdAt"> & { specifyText?: string | null; declined?: boolean | null })[],
  sectionId: string | undefined,
  keyByFieldId: ReadonlyMap<string, string>,
): SectionRow[] {
  if (!sectionId) return [];
  const byRow = new Map<string, { values: Record<string, string>; specify?: Record<string, string>; declined?: string[]; allHidden: boolean; first: number }>();
  for (const e of entries) {
    if (e.sectionId !== sectionId || !e.rowId) continue;
    const key = keyByFieldId.get(e.fieldId);
    if (!key) continue;
    const at = e.createdAt ? new Date(e.createdAt).getTime() : 0;
    const r = byRow.get(e.rowId) ?? { values: {}, allHidden: true, first: at };
    r.values[key] = e.value ?? "";
    if (e.specifyText) r.specify = { ...(r.specify ?? {}), [key]: e.specifyText };
    if (e.declined) r.declined = [...(r.declined ?? []), key];
    r.allHidden = r.allHidden && e.isHidden === true;
    r.first = Math.min(r.first, at);
    byRow.set(e.rowId, r);
  }
  return Array.from(byRow.entries())
    .sort((a, b) => a[1].first - b[1].first || a[0].localeCompare(b[0]))
    .map(([rowId, r]) => ({ rowId, hidden: r.allHidden, values: r.values, ...(r.specify ? { specify: r.specify } : {}), ...(r.declined ? { declined: r.declined } : {}) }));
}

/** `typed` (s229, D-229.68(b)): a typed section's row is saved as its typed record in ONE request
 *  (`/typed-rows`); hide / show stay per cell. The classic pages keep the cell path (D-229.68(c)). */
export function useSectionRows(clientId: string, categorySlug: string, sectionSlug: string, onAnswered: () => void, opts?: { typed?: boolean }) {
  const { categoryId, categoryType, sectionId, fieldIdByKey, fields } = useSectionFields(clientId, categorySlug, sectionSlug);
  const keyByFieldId = useMemo(() => {
    const m = new Map<string, string>();
    fieldIdByKey.forEach((id, key) => m.set(id, key));
    return m;
  }, [fieldIdByKey]);

  const { data: entries, isSuccess: entriesLoaded } = useQuery<GenericEntry[]>({
    queryKey: ["/api/clients", clientId, "generic-entries", categoryType],
    queryFn: async () => (await apiRequest("GET", `/api/clients/${clientId}/generic-entries?categoryType=${categoryType}`)).json(),
    enabled: !!clientId && !!categoryType,
  });
  const rows = useMemo(() => buildSectionRows(entries ?? [], sectionId, keyByFieldId), [entries, sectionId, keyByFieldId]);

  // s228 (INTERVIEW-SAVE-FAILURE): a refused save puts up the red pop-up (saveFailure.ts). The writers below still
  // throw, so a sequence stops at the first refusal and the box keeps what was typed.
  const onSaveFailed = useSaveFailure();
  const upsertMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      await queueRowSave(rowSaveKey(clientId, body.sectionId as string | undefined, body.rowId as string | undefined), () =>
        apiRequest("POST", `/api/clients/${clientId}/generic-entries`, body)),
    onError: onSaveFailed,
  });

  // Class C-server-deduped: a typed row upserts its record (by row id) and its cells (client, field, row). That holds
  // only when one fire lands before the next starts — two at once on a NEW cell both inserted and one failed (s236,
  // D-236.46) — so a row's requests go out one at a time (queueRowSave), and the server's insert is ON CONFLICT too.
  const typedMutation = useMutation({
    mutationFn: async (body: { rowId: string; cells: Array<{ fieldId: string; value: string }> }) =>
      await queueRowSave(rowSaveKey(clientId, sectionId, body.rowId), () =>
        apiRequest("POST", `/api/clients/${clientId}/typed-rows`, { sectionId, categoryId, ...body })),
    onError: onSaveFailed,
  });
  const typed = !!opts?.typed;
  const typedCells = (changes: Record<string, string>) =>
    Object.entries(changes).flatMap(([key, value]) => { const fieldId = fieldIdByKey.get(key); return fieldId ? [{ fieldId, value }] : []; });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "generic-entries"] });
    onAnswered();
  };

  const post = async (rowId: string, key: string, patch: { value?: string; isHidden?: boolean }) => {
    const fieldId = fieldIdByKey.get(key);
    if (!fieldId || !sectionId || !categoryId) return;
    await upsertMutation.mutateAsync({ categoryId, sectionId, fieldId, rowId, ...patch });
  };

  /** Write several cells of one row, then one refresh. */
  const writeCells = async (rowId: string, changes: Record<string, string>) => {
    if (typed) {
      const cells = typedCells(changes);
      if (cells.length && sectionId && categoryId) await typedMutation.mutateAsync({ rowId, cells });
    } else {
      for (const [key, value] of Object.entries(changes)) await post(rowId, key, { value });
    }
    refresh();
  };

  /** Hide or bring back every entry a row holds. Sends NO value. */
  const setRowHidden = async (rowId: string, hidden: boolean) => {
    const row = rows.find((r) => r.rowId === rowId);
    for (const key of Object.keys(row?.values ?? {})) await post(rowId, key, { isHidden: hidden });
  };

  /** Start a row with its first cells. The tap is the answer (D-200.2): the row exists as soon as it is asked for.
   *  Creating a row is Class A — callers run this inside their own single-flight lock. */
  const createRow = async (initial: Record<string, string>, rowId: string = crypto.randomUUID()) => {
    if (typed) {
      const cells = typedCells(initial);
      if (cells.length && sectionId && categoryId) await typedMutation.mutateAsync({ rowId, cells });
    } else {
      for (const [key, value] of Object.entries(initial)) await post(rowId, key, { value });
    }
    refresh();
    return rowId;
  };

  /** s232 (chapter two): a typed "Other" answer's words — the cell path's specify detail (OTHER-SPECIFY-1). The server
   *  keeps it only while the field's value is an Other choice. */
  const writeSpecify = async (rowId: string, key: string, text: string) => {
    const fieldId = fieldIdByKey.get(key);
    if (!fieldId || !sectionId || !categoryId) return;
    await upsertMutation.mutateAsync({ categoryId, sectionId, fieldId, rowId, specifyText: text });
    refresh();
  };

  return {
    // `ready` waits for the FIELDS too — rows are keyed by field, so with entries loaded and fields not, every
    // row reads empty and a screen seeding its tiles once would seed them from nothing (s221 staging look).
    ready: !!sectionId && !!categoryId && entriesLoaded && fieldIdByKey.size > 0,
    rows,
    fields,
    fieldIdByKey,
    writeCells,
    setRowHidden,
    createRow,
    refresh,
    writeSpecify,
  };
}
